import ShokzMember from "@theme/shokz-member";
import {
  pushHubSelectContent,
  pushHubModuleImpression,
} from "@theme/hub-ga4";
import { ref, createApp, onBeforeMount, computed, onUnmounted } from "vue";

/**
 * @import {Ref} from 'vue'
 * @import {Cart, CartItem} from './shopify-types'
 * @import {SimpleToast} from './component-simple-toast'
 * 
 * @typedef {import('./shokz-member-type').Coupon} Coupon
 */


/** @type {SimpleToast} */
const simpleToast = document.getElementById('simple-toast');

const cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

const HUB_GA4_CONTENT_TYPE = 'section';
const HUB_GA4_CONTENT_NAME = 'hub_benefits_section';
const HUB_EARLY_ACCESS_LINK_SELECTOR =
  '.hub-benefits__early-access__link, .hub-benefits__card-subtitle a';

let hubBenefitsPreviousState = window.__hubBenefitsPreviousState || {
  previousModule: '',
  previousModuleTimestamp: Date.now(),
};
window.__hubBenefitsPreviousState = hubBenefitsPreviousState;

function pushSelectContentEvent(buttonName) {
  pushHubSelectContent({
    contentType: HUB_GA4_CONTENT_TYPE,
    contentName: HUB_GA4_CONTENT_NAME,
    buttonName,
  });
}

function pushEarlyAccessSelectContentFromCard(card, options = {}) {
  if (!card?.hasAttribute('data-ga4-early-access')) return;

  const buttonName = options.isSubtitleLink
    ? card.dataset.ga4SubtitleButtonName || card.dataset.ga4ButtonName || ''
    : card.dataset.ga4ButtonName || '';

  pushHubSelectContent({
    contentType: HUB_GA4_CONTENT_TYPE,
    contentName: HUB_GA4_CONTENT_NAME,
    buttonName,
  });
}

function normalizeGa4Name(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const normalized = text.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('_');
}

function getPreviousModuleContext(fallbackModule) {
  if (typeof window.ga4GetPreviousModuleContext === 'function') {
    return window.ga4GetPreviousModuleContext(fallbackModule || '');
  }
  const state = hubBenefitsPreviousState || {};
  const previousModule =
    typeof state.previousModule === 'string' && state.previousModule
      ? state.previousModule
      : fallbackModule || '';
  const timestamp =
    typeof state.previousModuleTimestamp === 'number'
      ? state.previousModuleTimestamp
      : Date.now();
  return {
    previousModule,
    previousModuleDuration: String(Math.max(0, Math.round((Date.now() - timestamp) / 1000))),
  };
}

function updatePreviousModule(moduleName) {
  if (!moduleName) return;
  if (typeof window.ga4UpdatePreviousModule === 'function') {
    window.ga4UpdatePreviousModule(moduleName);
  }
  hubBenefitsPreviousState = {
    previousModule: moduleName,
    previousModuleTimestamp: Date.now(),
  };
  window.__hubBenefitsPreviousState = hubBenefitsPreviousState;
}

function pushModuleImpressionEvent(moduleName, fallbackModule) {
  if (!moduleName) return;
  const context = getPreviousModuleContext(fallbackModule || '');
  pushHubModuleImpression({
    module: moduleName,
    previousModuleDuration: context.previousModuleDuration,
    previousModule: context.previousModule,
  });
  updatePreviousModule(moduleName);
}

const ANCHOR_SCROLL_OFFSET = 100;

/** Referral 可兑换券 code 前缀，不在 hub-benefits 优惠券列表中展示 */
const REFERRAL_REDEMPTION_COUPON_PREFIX = 'US-FR-PD';
const LULULEMON_COUPON_PREFIX = 'US-LULU-OD';

function isLululemonCoupon(coupon) {
  return Boolean(coupon.shopify_coupon_code?.startsWith(LULULEMON_COUPON_PREFIX));
}

function couponDiscountPercentage(coupon) {
  return Number(coupon.coupon_name?.match(/(\d+(?:\.\d+)?)\s*%/)?.[1] || 0);
}

/**
 * @param {string | null | undefined} href
 * @returns {string | null} Target element id (without #)
 */
function getSamePageAnchorId(href) {
  if (!href) return null;
  const trimmed = href.trim();
  if (!trimmed || trimmed === '#') return null;

  if (trimmed.startsWith('#')) {
    return trimmed.slice(1) || null;
  }

  try {
    const linkUrl = new URL(trimmed, window.location.href);
    if (!linkUrl.hash || linkUrl.hash === '#') return null;

    const current = window.location;
    const isSamePage =
      linkUrl.hostname === current.hostname &&
      linkUrl.pathname === current.pathname &&
      linkUrl.search === current.search;

    return isSamePage ? linkUrl.hash.slice(1) : null;
  } catch (error) {
    return null;
  }
}

/**
 * @param {string} anchorId
 * @returns {boolean}
 */
function scrollToAnchor(anchorId) {
  const target = document.getElementById(anchorId);
  if (!target) return false;

  const top =
    target.getBoundingClientRect().top +
    window.scrollY -
    ANCHOR_SCROLL_OFFSET;

  window.scrollTo({
    top: Math.max(0, top),
    behavior: 'smooth',
  });
  return true;
}

/**
 * 创建 HubBenefits 的 Vue 实例
 * @param {HubBenefits} section 
 * @returns 
 */
function createHubBenefitsVueEntry(section) {
  const app = createApp({
    delimiters: ['${', '}'],
    setup() {
      const sectionData = section.data;
      // ============ 响应式数据 ============
      /**
       * @type {Ref<Coupon[], Coupon[]>}
       */
      const couponList = ref([]);
      const loading = ref(true);
      // 映射 ShokzMember 的会员等级
      const currentTier = ref(-1);
      /** @type {Ref<CartItem[]>} 购物车商品列表，侧边购物车的映射，需要跟随侧边购物车同步更新 */
      const cartItems = ref(sectionData?.cart_items ?? []);
      /**
       * @type {HTMLElement>}
       */
      const grid = ref(null);
      const isViewMore = ref(false);
      // 订单折扣券
      const orderDiscountCouponList = computed(() =>
        couponList.value
          ?.filter((coupon) => !!coupon.shopify_coupon_code
            && !coupon.shopify_coupon_code.startsWith(REFERRAL_REDEMPTION_COUPON_PREFIX)
            && coupon.expires_at
            && new Date(coupon.expires_at).getTime() > new Date().getTime())
          ?.map((coupon) => {
            const tryToFormatTierNum = Number(coupon.claimed_tier?.match(/\d+/g)?.join('') || -1);
            const tier_num = tryToFormatTierNum != -1 ? tryToFormatTierNum : window.ShokzMemberSettings.tiers.find((tier) => tier.tier_name == coupon.claimed_tier)?.tier || -1;
            return {
              ...coupon,
              tier_num: isNaN(tier_num) ? -1 : tier_num,
            }
          })
          ?.sort((a, b) => Number(isLululemonCoupon(b)) - Number(isLululemonCoupon(a))
            || couponDiscountPercentage(b) - couponDiscountPercentage(a)
            || b.tier_num - a.tier_num)
      );
      // 升级礼券（customer tags：ShokzSquad_gift_<tier>）
      // 排除 ShokzSquad_gift_CM*：芝加哥马拉松抽奖后端打标，非升级礼；否则会解析成 tier -1 → 空白 CLAIM 行
      const ugradeGiftCouponList = computed(() =>
        window.customer.tags.filter(
          (tag) =>
            tag.startsWith('ShokzSquad_gift_') && !tag.startsWith('ShokzSquad_gift_CM')
        )
          .map((tag) => {
            const tier_num = Number(tag.match(/\d+/g)?.join('') || -1);
            // 该礼券等级对应的礼品配置
            const giftConfig = sectionData?.gifts_config?.tiers?.find((tierGift) => tierGift.tier_num == tier_num);
            const tagAddedAt = window.customer.metafields?.[`tier_${tier_num}_tag_added_at`] ?? '';
            const expiresAt = tagAddedAt != '' ? new Date(tagAddedAt).getTime() + 63072000000 /* 24个月 */ : '';
            const alreadyInCart = cartItems.value.some((item) => item.product_id == giftConfig?.gift_id && item.properties['_ShokzSquad_gift_tier_'] == tier_num);
            return {
              expires_at: expiresAt,
              claimed_tier: tier_num,
              tier_num: Number(tag.match(/\d+/g)?.join('') || -1),
              gift_id: giftConfig?.gift_id,
              gift_variant_id: giftConfig?.gift_variant_id,
              gift_title: giftConfig?.gift_title,
              /** 是否已添加到购物车 */
              alreadyInCart: alreadyInCart,
            }
          })
          .sort((a, b) => b.tier_num - a.tier_num)
      );

      // 徽章角标类型的 css class
      const computedBadgeTypeClass = computed(() => (cardIndex) => {
        const settings = sectionData?.blocks[cardIndex];
        if (settings == undefined) return '';
        if (Number(settings.unlock_required_tier) < 0) {
          return 'badge--coming-soon';
        } else if (currentTier.value >= Number(settings.unlock_required_tier)) {
          // 满足解锁条件
          return 'badge--unlocked';
        } else {
          // 不满足解锁条件
          return 'badge--locked';
        }
      });
      // 徽章角标的文本
      const computedBadgeLabel = computed(() => (cardIndex) => {
        const settings = sectionData?.blocks[cardIndex];
        if (settings == undefined || ShokzMember?.memberData == undefined) return '--';
        if (Number(settings.unlock_required_tier) < 0) {
          return 'Coming soon';
        } else if (currentTier.value >= Number(settings.unlock_required_tier)) {
          // 满足解锁条件 
          return settings?.unlocked_badge_label?.replace(
            '${tier_name}',
            sectionData.tier_name_list[Number(settings.unlock_required_tier) - 1]
          );
        } else {
          // 不满足解锁条件 
          return settings?.locked_badge_label?.replace(
            '${tier_name}',
            sectionData.tier_name_list[Number(settings.unlock_required_tier) - 1]
          );
        }
      });

      /**
       * 购物车更新的订阅者
       * @type {Function}
       */
      let cartUpdateUnsubscriber;
      /**
       * 会员信息初始化后的订阅者
       * @type {Function}
       */
      let memberInfoInitializedUnsubscriber;
      // ============ 生命周期 ============
      onBeforeMount(() => {
        // reload();
        cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
        memberInfoInitializedUnsubscriber = subscribe(PUB_SUB_EVENTS.memberInfoInitialized, onMemberInfoInitialized);
      });
      onUnmounted(() => {
        if (cartUpdateUnsubscriber) {
          cartUpdateUnsubscriber();
        }
        if (memberInfoInitializedUnsubscriber) {
          memberInfoInitializedUnsubscriber();
        }
      });

      // ============ 方法 ============
      /**
       * 会员信息初始化后加载模块
       * @param {{data: import("./shokz-member-type").MemberData}} data 
       */
      const onMemberInfoInitialized = (data) => {
        reload();
      }
      /**
       * 购物车更新的回调函数
       * @param {{source: string, productVariantId: number, cartData: Cart}} data 
       */
      const onCartUpdate = (data) => {
        if (data?.cartData?.items == undefined) return;
        console.log('onCartUpdate', data);
        if (data.source == 'cart-items') {
          // 侧边购物车更改 quantity 时触发
          cartItems.value = data.cartData.items;
        } else if (data.source == 'hub-benefits') {
          // 当前页面加购礼品时触发
          const foundItemIndex = cartItems.value.findIndex((item) => item.product_id == data.productVariantId);
          if (foundItemIndex != -1) {
            cartItems.value[foundItemIndex] = data.cartData.items[0];
          } else {
            cartItems.value.push(...data.cartData.items);
          }
          $('.cart-count-update span').text(data.cartData.item_count);
        }
        console.log('[onCartUpdate] cartItems', cartItems.value);
      }

      const reload = () => {
        loading.value = true;
        fetchData().catch((err) => {
          console.error('[section-hub-benefits]: fetchData error', err);
          setTimeout(reload, 5000);
        }).finally(() => {
          loading.value = false;
        });
      }

      const fetchData = async () => {
        const memberData = await ShokzMember.getMemberDataByOnceCall();
        const tierNum = ShokzMember.getTierNumber();
        currentTier.value = tierNum;
        const res = await ShokzMember.getMemberCoupons();
        couponList.value = res?.data ?? [];
      }

      const onClickViewMore = () => {
        pushSelectContentEvent('View_More');
        grid.value.classList.add('grid--view-more');
        isViewMore.value = true;
      };

      const onClickViewLess = () => {
        pushSelectContentEvent('View_More');
        grid.value.classList.remove('grid--view-more');
        isViewMore.value = false;
      };

      // ============ 返回值 ============
      // 返回所有需要在模板中使用的响应式数据和方法
      return {
        loading,
        couponList,
        currentTier,
        grid,
        orderDiscountCouponList,
        ugradeGiftCouponList,
        computedBadgeTypeClass,
        computedBadgeLabel,
        isViewMore,
        onClickViewMore,
        onClickViewLess,
      };
    },
    compilerOptions: {
      // 标记哪些标签是 WebComponents，避免和 Vue 冲突
      isCustomElement: (tag) => ['hub-benefits-card', 'common-popup', 'hub-benefits-scrollbar'].includes(tag),
    }
  });

  const couponsBlankTemplateEl = section.querySelector('[data-coupons-blank-template]');
  app.component('vue-coupons-blank', {
    delimiters: ['${', '}'],
    template: couponsBlankTemplateEl?.innerHTML?.trim() || '<div></div>',
  });

  app.component('vue-coupon-item', {
    delimiters: ['${', '}'],
    props: [
      'coupon',
      /** 优惠券类型（对应 USE NOW 按钮的使用模式），取值有：'offers_discount' 折扣券，'upgrade_gift' 礼品券 */
      'couponType',
      // 是否禁用
      'disabled'
    ],
    template: section.querySelector('[data-coupon-template]').innerHTML,
    setup() {
      const loading = ref(false);
      const couponTierName = computed(() => (coupon) => {
        if (isLululemonCoupon(coupon)) return section.data.lululemon_coupon_label;
        return window.ShokzMemberSettings.tiers.find((tier) => tier.tier == coupon.tier_num)?.tier_name || coupon.claimed_tier || '';
      });

      /**
       * 加购
       * @param {{id: number, quantity: number, properties: Object}[]} items
       * @returns 
       */
      const addToCart = async (items) => {
        const formData = {
          items: items,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        };
        const data = JSON.stringify(formData);
        const response = await fetch(window.routes.cart_add_url,
          {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: data
          }
        );
        if (response.ok) {
          return await response.json();
        }
        let responseData = null;
        try {
          responseData = await response.json();
        } catch (error) {
          // 不需要做处理
        }
        if (response.status == 422 && responseData?.message?.includes('eligible limit')) {
          // 注：Cart checkout validation function 的错误也会走 422 错误码
          throw new Error(responseData?.message);
        } else if (response.status == 422) {
          throw new Error("This upgrade gift is currently out of stock.");
        } else if (response.status == 429) {
          throw new Error("Too Many Requests, please refresh the page or try again later.");
        } else if (!!responseData?.message) {
          // 注：Cart checkout validation function 的错误也会走 422 错误码
          throw new Error(responseData?.message);
        } else {
          throw new Error(`An unknown error occurred, please try again later. Error code: ${response.status}.`);
        }
      }
      /**
       * 更新购物车，并弹出侧边购物车
       * @returns 
       */
      const updateCart = async (variantId, response) => {
        cartElement?.renderContents(response);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'hub-benefits',
          productVariantId: variantId,
          cartData: response,
        });
      }

      /**
       * 使用优惠券
       * @param {Coupon} coupon 
       * @param {string} couponType 
       * @returns 
       */
      const onClickCouponUseNow = (coupon, couponType) => {
        if (couponType == 'offers_discount') {
          const discountStrength = String(coupon?.coupon_name || '').match(/\d+/)?.[0] || '';
          const buttonName = discountStrength ? `Discounts_${discountStrength}` : 'Discounts';
          pushSelectContentEvent(buttonName);
          return;
        }
        if (couponType != 'upgrade_gift') return;
        if (loading.value) return;
        console.log('onClickCouponUseNow', coupon);
        const giftName = normalizeGa4Name(coupon?.gift_title || '');
        pushSelectContentEvent(giftName ? `Gifts_${giftName}` : 'Gifts');
        // 实物礼券的点击事件
        loading.value = true;
        const variantId = coupon.gift_variant_id[0];
        const tier_num = coupon.tier_num ?? Number(coupon.claimed_tier?.match(/\d+/g)?.join('') || -1);
        addToCart([{ id: variantId, quantity: 1, properties: { _ShokzSquad_gift_tier_: coupon.tier_num, 'ShokzSquad Gift': window.ShokzMemberSettings.tiers.find((tier) => tier.tier == tier_num)?.tier_name } }]).then((res) => {
          console.log(res);
          return updateCart(variantId, res);
        }).catch((err) => {
          console.error('[section-hub-benefits]: addToCart error', err);
          simpleToast.show(err?.message || 'An unknown error occurred, please try again later.');
        }).finally(() => {
          loading.value = false;
        })
      };

      return {
        loading,
        couponTierName,
        onClickCouponUseNow,
      }
    }
  });

  app.mount(section.querySelector('[data-vue-render]'));
  return app;
}

class HubBenefits extends HTMLElement {
  /** @type { {gifts_config: {tiers: {tier_num: number, gift_id: number, gift_title: string, gift_variant_id: number[]}[]}, tier_name_list: string[], blocks: {discount_type: string, unlock_required_tier: string, locked_badge_label: string, unlocked_badge_label: string}[]} } */
  data = undefined;

  constructor() {
    super();
    const scriptElement = this.querySelector('[data-json]');
    this.data = JSON.parse(scriptElement.innerHTML);
    createHubBenefitsVueEntry(this);
  }
}

class HubBenefitsCard extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this._onClickPopupTigger = this.onClickPopupTigger.bind(this);
    this._onClickPopupButton = this.onClickPopupButton.bind(this);
    this._onPopupMutate = this.onPopupMutate.bind(this);
    this._onContentLinkClick = this.onContentLinkClick.bind(this);
    this._onGa4EarlyAccessClick = this.onGa4EarlyAccessClick.bind(this);
    this._tracksEarlyAccess = this.hasAttribute('data-ga4-early-access');

    this.cardElements = {
      popupTigger: this.querySelector('[data-popup-tigger]'),
      /** @type {HubBenefitsPopup} */
      popup: this.querySelector('[data-popup]'),
      popupButton: this.querySelector('[data-popup-button-ga4]'),
      title: this.querySelector('.hub-benefits__card-title'),
      contentLink: this.querySelector('.hub-benefits__card-content-link'),
    };
    this._impressionTimer = null;
    this._hasPopupImpressionFired = false;
    this.popupObserver = null;
    this.attachListener();
  }

  disconnectedCallback() {
    this.clearPopupImpressionTimer();
    this.popupObserver?.disconnect();
    this.popupObserver = null;
    this.detachListener();
  }

  attachListener() {
    this.cardElements.popupTigger.addEventListener('click', this._onClickPopupTigger);
    this.cardElements.popupButton?.addEventListener('click', this._onClickPopupButton);
    this.cardElements.contentLink?.addEventListener('click', this._onContentLinkClick);
    if (this._tracksEarlyAccess) {
      this.addEventListener('click', this._onGa4EarlyAccessClick);
    }
    if (this.cardElements.popup) {
      this.popupObserver = new MutationObserver(this._onPopupMutate);
      this.popupObserver.observe(this.cardElements.popup, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }
  }

  detachListener() {
    this.cardElements.popupTigger.removeEventListener('click', this._onClickPopupTigger);
    this.cardElements.popupButton?.removeEventListener('click', this._onClickPopupButton);
    this.cardElements.contentLink?.removeEventListener('click', this._onContentLinkClick);
    if (this._tracksEarlyAccess) {
      this.removeEventListener('click', this._onGa4EarlyAccessClick);
    }
  }

  onGa4EarlyAccessClick(event) {
    const link = event.target.closest(HUB_EARLY_ACCESS_LINK_SELECTOR);
    if (!link) return;
    const isSubtitleLink = !!event.target.closest('.hub-benefits__card-subtitle a');
    pushEarlyAccessSelectContentFromCard(this, { isSubtitleLink });
  }

  onContentLinkClick(event) {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    if (this.classList.contains('referral_benefit_card')) {
      pushSelectContentEvent('Copy_Referral Rewards');
    }

    const anchorId = getSamePageAnchorId(link.getAttribute('href'));
    if (!anchorId) return;

    if (!scrollToAnchor(anchorId)) return;

    event.preventDefault();
    if (window.history?.replaceState) {
      window.history.replaceState(null, '', `#${anchorId}`);
    }
  }

  clearPopupImpressionTimer() {
    if (this._impressionTimer) {
      window.clearTimeout(this._impressionTimer);
      this._impressionTimer = null;
    }
  }

  startPopupImpressionTimer() {
    if (this._hasPopupImpressionFired || this._impressionTimer) return;
    const isReferralCard = this.classList.contains('referral_benefit_card');
    const impressionDelay = isReferralCard ? 1000 : 3000;
    this._impressionTimer = window.setTimeout(() => {
      this._impressionTimer = null;
      if (!this.cardElements.popup?.hasAttribute('open')) return;
      const moduleName = isReferralCard
        ? 'Referral Rewards'
        : normalizeGa4Name(this.cardElements.title?.textContent || '');
      if (!moduleName) return;
      this._hasPopupImpressionFired = true;
      pushModuleImpressionEvent(moduleName, 'hub_benefits_section');
    }, impressionDelay);
  }

  onPopupMutate() {
    if (!this.cardElements.popup) return;
    if (!this.cardElements.popup.hasAttribute('open')) {
      this.clearPopupImpressionTimer();
      return;
    }
    this.startPopupImpressionTimer();
  }

  onClickPopupTigger() {
    const buttonName = this.classList.contains('referral_benefit_card')
      ? 'Referral Rewards'
      : normalizeGa4Name(this.cardElements.title?.textContent || '');
    if (buttonName) pushSelectContentEvent(buttonName);
    this.cardElements.popup.open();
    this.startPopupImpressionTimer();
  }

  onClickPopupButton() {
    const buttonName =
      this.cardElements.popupButton?.getAttribute('data-popup-button-ga4') ||
      normalizeGa4Name(this.cardElements.popupButton?.textContent || '');
    if (!buttonName) return;
    pushSelectContentEvent(buttonName);
  }
}

if (!customElements.get('hub-benefits')) {
  customElements.define('hub-benefits', HubBenefits);
}
if (!customElements.get('hub-benefits-card')) {
  customElements.define('hub-benefits-card', HubBenefitsCard);
}
